import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { AlertTriangle, Plus, RotateCw } from "lucide-react";
import { showErrorAlert } from "../../../../../components/HelperAlerts/HelpersAlerts.jsx";

const EMPTY_FORM = { name: "", technology: "bambu", model_name: "", orca_profile: "", is_active: true, notes: "" };

const formatApiError = (err, fallback) => {
    const data = err.response?.data;
    if (!data) return fallback;
    if (typeof data === "string") return data;
    if (data.detail) return data.detail;
    return Object.entries(data)
        .map(([field, value]) => `${field}: ${Array.isArray(value) ? value.join(", ") : value}`)
        .join(" | ");
};

export default function ImpresorasSection() {
    const [printers, setPrinters] = useState([]);
    const [references, setReferences] = useState([]);
    const [state, setState] = useState("loading");
    const [form, setForm] = useState(EMPTY_FORM);
    const [showForm, setShowForm] = useState(false);
    const [saving, setSaving] = useState(false);
    const [loadForm, setLoadForm] = useState({}); // printerId -> filament_reference id

    const load = useCallback(() => {
        setState("loading");
        Promise.all([
            axios.get("/fulfillment-admin/printers/"),
            axios.get("/fulfillment-admin/filaments/", { params: { is_active: true } }),
        ])
            .then(([printersRes, refsRes]) => {
                setPrinters(Array.isArray(printersRes.data) ? printersRes.data : []);
                setReferences(Array.isArray(refsRes.data) ? refsRes.data : []);
                setState("ready");
            })
            .catch((err) => {
                console.error("Error loading printers:", err);
                setState(err.response?.status === 403 ? "forbidden" : "error");
            });
    }, []);

    useEffect(() => { load(); }, [load]);

    const handleCreate = async (event) => {
        event.preventDefault();
        setSaving(true);
        try {
            await axios.post("/fulfillment-admin/printers/", form);
            setForm(EMPTY_FORM);
            setShowForm(false);
            load();
        } catch (err) {
            console.error("Error creating printer:", err);
            showErrorAlert({ title: "No se pudo crear la impresora", text: formatApiError(err, "Revisa los datos.") });
        } finally {
            setSaving(false);
        }
    };

    const toggleActive = async (printer) => {
        try {
            await axios.patch(`/fulfillment-admin/printers/${printer.id}/`, { is_active: !printer.is_active });
            load();
        } catch (err) {
            console.error("Error toggling printer:", err);
            showErrorAlert({ title: "No se pudo actualizar", text: formatApiError(err, "Inténtalo de nuevo.") });
        }
    };

    const confirmColorLoad = async (printer) => {
        const filamentReferenceId = loadForm[printer.id];
        if (!filamentReferenceId) return;
        try {
            await axios.post(`/fulfillment-admin/printers/${printer.id}/color-loads/`, {
                filament_reference: filamentReferenceId, is_loaded: true,
            });
            load();
        } catch (err) {
            console.error("Error confirming color load:", err);
            showErrorAlert({ title: "No se pudo confirmar el color", text: formatApiError(err, "Inténtalo de nuevo.") });
        }
    };

    if (state === "loading") {
        return <div className="flex justify-center py-10"><span className="loading loading-spinner loading-lg text-primary" /></div>;
    }
    if (state === "forbidden") {
        return (
            <div role="alert" className="alert alert-warning text-sm">
                <AlertTriangle className="w-4 h-4" />
                Tu cuenta no tiene el permiso <code>fulfillment.manage_printers</code>.
            </div>
        );
    }
    if (state === "error") {
        return (
            <div role="alert" className="alert alert-error text-sm">
                <AlertTriangle className="w-4 h-4" />
                No se pudieron cargar las impresoras.
                <button type="button" className="btn btn-xs gap-1" onClick={load}><RotateCw className="w-3 h-3" />Reintentar</button>
            </div>
        );
    }

    return (
        <div className="flex flex-col gap-4">
            <div className="flex items-center justify-between">
                <p className="text-sm text-base-content/60">
                    Flota Bambu/Klipper registrada manualmente. Ninguna acción de aquí controla la máquina.
                </p>
                <button type="button" className="btn btn-sm btn-primary gap-1" onClick={() => setShowForm((v) => !v)}>
                    <Plus className="w-4 h-4" /> Nueva impresora
                </button>
            </div>

            {showForm && (
                <form onSubmit={handleCreate} className="glassCard rounded-xl p-4 grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <label className="form-control gap-1">
                        <span className="label-text text-xs">Nombre</span>
                        <input required className="input input-bordered input-sm" value={form.name}
                            onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} />
                    </label>
                    <label className="form-control gap-1">
                        <span className="label-text text-xs">Tecnología</span>
                        <select className="select select-bordered select-sm" value={form.technology}
                            onChange={(e) => setForm((f) => ({ ...f, technology: e.target.value }))}>
                            <option value="bambu">Bambu</option>
                            <option value="klipper">Klipper</option>
                        </select>
                    </label>
                    <label className="form-control gap-1">
                        <span className="label-text text-xs">Modelo</span>
                        <input className="input input-bordered input-sm" value={form.model_name}
                            onChange={(e) => setForm((f) => ({ ...f, model_name: e.target.value }))} />
                    </label>
                    <label className="form-control gap-1">
                        <span className="label-text text-xs">Perfil Orca</span>
                        <input className="input input-bordered input-sm" value={form.orca_profile}
                            onChange={(e) => setForm((f) => ({ ...f, orca_profile: e.target.value }))} />
                    </label>
                    <div className="sm:col-span-2 flex justify-end gap-2">
                        <button type="button" className="btn btn-sm btn-ghost" onClick={() => setShowForm(false)}>Cancelar</button>
                        <button type="submit" className="btn btn-sm btn-primary" disabled={saving}>
                            {saving ? <span className="loading loading-spinner loading-xs" /> : "Crear"}
                        </button>
                    </div>
                </form>
            )}

            {printers.length === 0 ? (
                <p className="text-sm text-base-content/50">No hay impresoras registradas todavía.</p>
            ) : (
                <ul className="flex flex-col gap-3">
                    {printers.map((printer) => (
                        <li key={printer.id} className="glassCard rounded-xl p-4 flex flex-col gap-3">
                            <div className="flex items-center justify-between flex-wrap gap-2">
                                <div>
                                    <p className="font-semibold">{printer.name}</p>
                                    <p className="text-xs text-base-content/60">
                                        {printer.technology === "bambu" ? "Bambu" : "Klipper"} · {printer.model_name || "sin modelo"}
                                    </p>
                                </div>
                                <button
                                    type="button"
                                    className={`btn btn-xs ${printer.is_active ? "btn-success" : "btn-ghost"}`}
                                    onClick={() => toggleActive(printer)}
                                >
                                    {printer.is_active ? "Activa" : "Inactiva"}
                                </button>
                            </div>

                            <div>
                                <p className="text-xs text-base-content/55 mb-1">Colores cargados</p>
                                {(printer.color_loads || []).length === 0 ? (
                                    <p className="text-xs text-base-content/40">Sin colores confirmados.</p>
                                ) : (
                                    <div className="flex flex-wrap gap-1.5">
                                        {printer.color_loads.map((load) => (
                                            <span key={load.id} className="badge badge-sm gap-1" title={new Date(load.confirmed_at).toLocaleString("es-ES")}>
                                                <span className="w-3 h-3 rounded-full border border-base-300" style={{ backgroundColor: load.filament_reference_detail?.color_hex }} />
                                                {load.filament_reference_detail?.display_name}
                                            </span>
                                        ))}
                                    </div>
                                )}
                                <div className="flex gap-2 mt-2">
                                    <select
                                        className="select select-bordered select-xs flex-1"
                                        value={loadForm[printer.id] || ""}
                                        onChange={(e) => setLoadForm((f) => ({ ...f, [printer.id]: e.target.value }))}
                                    >
                                        <option value="">Confirmar color cargado…</option>
                                        {references.map((ref) => (
                                            <option key={ref.id} value={ref.id}>{ref.display_name}</option>
                                        ))}
                                    </select>
                                    <button type="button" className="btn btn-xs btn-outline" onClick={() => confirmColorLoad(printer)}>
                                        Confirmar
                                    </button>
                                </div>
                            </div>
                        </li>
                    ))}
                </ul>
            )}
        </div>
    );
}
